import { useEffect, useLayoutEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
    "a[href]",
    "area[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "iframe",
    "object",
    "embed",
    "[contenteditable='true']",
    "[tabindex]",
].join(",");

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const isHiddenByStyle = (element) => {
    const ownerWindow = element.ownerDocument?.defaultView;
    if (!ownerWindow?.getComputedStyle) {
        return false;
    }

    const elementStyle = ownerWindow.getComputedStyle(element);
    if (elementStyle.visibility === "hidden" || elementStyle.visibility === "collapse") {
        return true;
    }

    let current = element;
    while (current) {
        if (ownerWindow.getComputedStyle(current).display === "none") {
            return true;
        }
        current = current.parentElement;
    }

    return false;
};

const isFocusable = (element) => {
    if (!element || typeof element.focus !== "function") {
        return false;
    }
    if (element.disabled || element.getAttribute("aria-hidden") === "true") {
        return false;
    }
    if (element.closest("[hidden], [inert], [aria-hidden='true']")) {
        return false;
    }
    if (isHiddenByStyle(element)) {
        return false;
    }
    const tabIndex = Number(element.getAttribute("tabindex"));
    if (Number.isFinite(tabIndex) && tabIndex < 0) {
        return false;
    }
    return element.tabIndex >= 0;
};

export const getFocusableElements = (container) => {
    if (!container) {
        return [];
    }
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isFocusable);
};

const focusElement = (element) => {
    try {
        element?.focus?.({ preventScroll: true });
    } catch {
        element?.focus?.();
    }
};

const getDocument = (container) => container?.ownerDocument || document;

/**
 * Keeps keyboard focus within an active floating surface and restores focus to
 * the opener when that surface closes.
 *
 * Overlays a trapped surface opens (dropdown menus, popovers) must render inside
 * the trapped container — portal them to a host within it, not to the global
 * `#portal` — or they fall outside both this ring and, for `aria-modal`
 * dialogs, the subtree assistive tech exposes.
 */
export default function useFocusTrap({
    active = true,
    containerRef,
    initialFocusRef,
    restoreFocus = true,
} = {}) {
    const previousFocusRef = useRef(null);

    useIsomorphicLayoutEffect(() => {
        const container = containerRef?.current;
        if (!active || !container) {
            return undefined;
        }

        const ownerDocument = getDocument(container);
        const previousFocus = ownerDocument.activeElement;
        previousFocusRef.current = previousFocus && previousFocus !== ownerDocument.body
            ? previousFocus
            : null;

        const hadTabIndex = container.hasAttribute("tabindex");
        if (!hadTabIndex) {
            container.setAttribute("tabindex", "-1");
        }

        if (!container.contains(ownerDocument.activeElement)) {
            const focusTarget = initialFocusRef?.current || getFocusableElements(container)[0] || container;
            focusElement(focusTarget);
        }

        const handleKeyDown = (event) => {
            if (event.key !== "Tab" || event.defaultPrevented) {
                return;
            }

            const focusableElements = getFocusableElements(container);
            if (focusableElements.length === 0) {
                event.preventDefault();
                focusElement(container);
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const activeElement = ownerDocument.activeElement;
            const isFocusInside = container.contains(activeElement);

            if (event.shiftKey) {
                if (!isFocusInside || activeElement === firstElement) {
                    event.preventDefault();
                    focusElement(lastElement);
                }
                return;
            }

            if (!isFocusInside || activeElement === lastElement) {
                event.preventDefault();
                focusElement(firstElement);
            }
        };

        container.addEventListener("keydown", handleKeyDown);

        return () => {
            container.removeEventListener("keydown", handleKeyDown);
            if (!hadTabIndex) {
                container.removeAttribute("tabindex");
            }

            const previousFocusedElement = previousFocusRef.current;
            if (
                restoreFocus
                && previousFocusedElement
                && previousFocusedElement.isConnected
                && typeof previousFocusedElement.focus === "function"
            ) {
                focusElement(previousFocusedElement);
            }
        };
    }, [active, containerRef, initialFocusRef, restoreFocus]);
}
